import { Link } from 'wouter';
import type { Contest } from '@anuleaza/shared';
import { Pill } from '../primitives/Pill.js';

interface Props {
  contest: Contest;
}

const STATUS_LABEL: Partial<Record<Contest['status'], string>> = {
  draft: 'în lucru',
  ocr_pending: 'AI citește',
  ocr_done: 'date extrase',
  ocr_failed: 'eroare OCR',
  answering: 'răspunzi întrebări',
  analyzing: 'AI analizează',
  analyzed: 'rezultat gata',
  awaiting_payment: 'așteaptă plată',
  paying: 'plată în curs',
  paid: 'plătit',
  generating: 'generez plângerea',
  ready: 'PDF gata',
  delivered: 'trimis pe email',
  filed: 'depus la instanță',
  won: 'câștigat',
  lost: 'pierdut',
  pending_judgement: 'în judecată',
};

const STATUS_TONE: Partial<Record<Contest['status'], { bg: string; color: string; dot?: string }>> = {
  ocr_pending: { bg: '#FFF8E5', color: '#5C4A0E', dot: '#FFC857' },
  ocr_done: { bg: '#F4F2EB', color: '#1F1F1B' },
  ready: { bg: 'oklch(0.94 0.06 145)', color: 'oklch(0.52 0.18 145)', dot: 'oklch(0.72 0.18 145)' },
  filed: { bg: 'oklch(0.94 0.06 145)', color: 'oklch(0.52 0.18 145)', dot: 'oklch(0.72 0.18 145)' },
  won: { bg: 'oklch(0.94 0.06 145)', color: 'oklch(0.52 0.18 145)', dot: 'oklch(0.72 0.18 145)' },
  lost: { bg: '#FCE4E6', color: '#A12126', dot: '#E5484D' },
};

function shortId(id: string): string {
  return id.slice(-5).toUpperCase();
}

/** Pick the most relevant screen for this contest's current state. */
function bestHref(contest: Contest): string {
  if (contest.analysis_json) return `/contest/${contest.id}/result`;
  if (contest.extracted_json) return `/contest/${contest.id}/confirm`;
  return `/contest/${contest.id}/scan`;
}

export function ContestRow({ contest }: Props) {
  const label = STATUS_LABEL[contest.status] ?? contest.status;
  const tone = STATUS_TONE[contest.status] ?? { bg: '#F4F2EB', color: '#1F1F1B' };
  const fapta = contest.extracted_json?.fapta;
  const tip = fapta?.tip ?? '—';
  const desc = fapta?.descriere ?? 'PV în analiză';
  const locatie = fapta?.locatie;

  return (
    <Link
      href={bestHref(contest)}
      className="flex items-center justify-between gap-4 bg-card border border-line rounded-xl px-5 py-4 hover:border-fg-2 transition"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-mono text-xs text-muted">#{shortId(contest.id)}</span>
          <span className="text-xs text-faint">·</span>
          <span className="text-xs text-muted capitalize">{tip}</span>
        </div>
        <div className="text-sm font-medium text-fg-2 truncate">{desc}</div>
        {locatie && <div className="text-xs text-muted mt-1 truncate">{locatie}</div>}
      </div>
      <div className="flex flex-col items-end gap-1 shrink-0">
        <Pill bg={tone.bg} color={tone.color} dot={tone.dot}>
          {label}
        </Pill>
        {contest.fine_amount_lei && (
          <span className="text-xs font-mono text-muted">
            {contest.fine_amount_lei.toLocaleString('ro-RO')} lei
          </span>
        )}
      </div>
    </Link>
  );
}
